import { createFileRoute } from "@tanstack/react-router"
import {
  Activity,
  Bot,
  Boxes,
  Building2,
  Database,
  MessagesSquare,
  OctagonX,
  Radar,
  TriangleAlert,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { type MonitorScope, useKillAll, useMonitorEvents, useMonitorSnapshot } from "@/api/queries/monitor"
import { useSession } from "@/api/queries/session"
import { useConfirm } from "@/components/app/confirm-dialog"
import { PageContainer, PageHeader, PageSection } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { StatCard } from "@/components/app/stat-card"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ControllerPanel, useControllerChat } from "@/features/insights/controller-panel"
import { monitorCounts } from "@/features/insights/monitor-logic"
import { MonitorRunSheet } from "@/features/insights/monitor-run-sheet"
import { ChatSessionList, MonitorRunsTable, RoomList } from "@/features/insights/monitor-sections"
import { useMediaQuery } from "@/hooks/use-media-query"
import { can } from "@/lib/permissions"

interface MonitorSearch {
  /** `platform`: every organization (platform admins). */
  scope?: "platform"
  /** uid of the run open in the live drawer */
  run?: string
}

export const Route = createFileRoute("/_app/insights/monitor")({
  staticData: { titleKey: "nav.items.monitor" },
  validateSearch: (s: Record<string, unknown>): MonitorSearch => ({
    scope: s.scope === "platform" ? "platform" : undefined,
    run: typeof s.run === "string" && s.run ? s.run : undefined,
  }),
  component: MonitorPage,
})

function ConnectionBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  if (status === "open")
    return (
      <StatusBadge tone="success" dot pulse>
        {t("insights.monitor.connection.live")}
      </StatusBadge>
    )
  if (status === "reconnecting")
    return (
      <StatusBadge tone="warning" dot>
        {t("insights.monitor.connection.reconnecting")}
      </StatusBadge>
    )
  if (status === "connecting")
    return (
      <StatusBadge tone="neutral" dot>
        {t("insights.monitor.connection.connecting")}
      </StatusBadge>
    )
  return (
    <StatusBadge tone="neutral" dot>
      {t("insights.monitor.connection.polling")}
    </StatusBadge>
  )
}

function MonitorPage() {
  const { t } = useTranslation()
  const session = useSession()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const confirm = useConfirm()
  const isPlatformAdmin = can(session, "platformAdmin")
  const scope: MonitorScope = search.scope === "platform" && isPlatformAdmin ? "platform" : "tenant"

  const connection = useMonitorEvents(scope)
  const snapshot = useMonitorSnapshot(scope, { poll: connection !== "open" })
  const counts = monitorCounts(snapshot.data)
  const killAll = useKillAll()
  const canKill = scope === "platform" ? isPlatformAdmin : can(session, "admin")

  const wide = useMediaQuery("(min-width: 1280px)")
  const chat = useControllerChat()
  const [chatOpen, setChatOpen] = useState(false)

  const setSearch = (patch: Partial<MonitorSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const openRun = search.run ? snapshot.data?.runs.find((r) => r.uid === search.run) : undefined

  const onKillAll = () =>
    void confirm({
      title: t(
        scope === "platform" ? "insights.monitor.killAll.titlePlatform" : "insights.monitor.killAll.title",
      ),
      description: t(
        scope === "platform"
          ? "insights.monitor.killAll.descriptionPlatform"
          : "insights.monitor.killAll.description",
        { runs: counts.runs, rooms: counts.rooms },
      ),
      confirmText: t("insights.monitor.killAll.confirmWord"),
      confirmLabel: t("insights.monitor.killAll.action"),
      destructive: true,
      onConfirm: () => killAll.mutateAsync(scope),
    })

  const health = snapshot.data?.health

  return (
    <PageContainer width="full" className="max-w-[100rem]">
      <PageHeader
        title={t("insights.monitor.title")}
        description={t("insights.monitor.description")}
        icon={Radar}
        actions={
          <>
            {!wide && (
              <Button variant="outline" onClick={() => setChatOpen(true)}>
                <Bot />
                {t("insights.monitor.controller.open")}
                {chat.state.live && <span className="bg-primary size-1.5 animate-pulse rounded-full" />}
              </Button>
            )}
            {canKill && (
              <Button
                variant="destructive"
                onClick={onKillAll}
                disabled={counts.active === 0 || killAll.isPending}
                data-testid="kill-all"
              >
                <OctagonX />
                {counts.active > 0
                  ? t("insights.monitor.killAll.buttonCount", { count: counts.active })
                  : t("insights.monitor.killAll.button")}
              </Button>
            )}
          </>
        }
      >
        {isPlatformAdmin && (
          <Tabs
            value={scope}
            onValueChange={(v) =>
              setSearch({ scope: v === "platform" ? "platform" : undefined, run: undefined })
            }
          >
            <TabsList variant="line" className="h-8">
              <TabsTrigger value="tenant" className="px-2">
                <Building2 />
                {t("insights.monitor.scope.tenant")}
              </TabsTrigger>
              <TabsTrigger value="platform" className="px-2">
                <Radar />
                {t("insights.monitor.scope.platform")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </PageHeader>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-8">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <ConnectionBadge status={connection} />
            {health && (
              <StatusBadge tone={health.database_ok ? "success" : "danger"}>
                <Database />
                {health.database_ok ? t("insights.monitor.health.dbOk") : t("insights.monitor.health.dbDown")}
              </StatusBadge>
            )}
            {health && (
              <StatusBadge tone="neutral">
                {t("insights.monitor.health.nodeRuns", { count: health.node_active_runs })}
              </StatusBadge>
            )}
            {snapshot.data && (
              <span className="text-muted-foreground">
                {t("insights.monitor.updated")} <RelativeTime value={snapshot.data.generated_at} />
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label={t("insights.monitor.kpi.runs")}
              value={counts.runs}
              icon={Activity}
              loading={snapshot.isPending}
            />
            <StatCard
              label={t("insights.monitor.kpi.sessions")}
              value={counts.chatSessions}
              icon={MessagesSquare}
              loading={snapshot.isPending}
            />
            <StatCard
              label={t("insights.monitor.kpi.rooms")}
              value={counts.rooms}
              icon={Boxes}
              loading={snapshot.isPending}
            />
            <StatCard
              label={t("insights.monitor.kpi.stale")}
              value={counts.stale}
              icon={TriangleAlert}
              hint={t("insights.monitor.kpi.staleHint")}
              loading={snapshot.isPending}
              className={counts.stale > 0 ? "border-warning/40" : undefined}
            />
          </div>

          <PageSection
            title={t("insights.monitor.sections.runs")}
            description={t("insights.monitor.sections.runsHint")}
          >
            <MonitorRunsTable
              runs={snapshot.data?.runs}
              receivedAt={snapshot.dataUpdatedAt}
              scope={scope}
              loading={snapshot.isPending}
              error={snapshot.error}
              onRetry={() => void snapshot.refetch()}
              onOpen={(uid) => setSearch({ run: uid })}
              activeUid={search.run}
            />
          </PageSection>

          <PageSection title={t("insights.monitor.sections.sessions")}>
            <ChatSessionList runs={snapshot.data?.runs ?? []} scope={scope} />
          </PageSection>

          <PageSection title={t("insights.monitor.sections.rooms")}>
            <RoomList rooms={snapshot.data?.rooms ?? []} />
          </PageSection>
        </div>

        {wide && (
          <ControllerPanel chat={chat} className="sticky top-6 h-[min(46rem,calc(100dvh-8rem))] self-start" />
        )}
      </div>

      {!wide && (
        <Sheet open={chatOpen} onOpenChange={setChatOpen}>
          <SheetContent className="gap-0 p-0 sm:max-w-md">
            <SheetHeader className="sr-only">
              <SheetTitle>{t("insights.monitor.controller.title")}</SheetTitle>
              <SheetDescription>{t("insights.monitor.controller.intro")}</SheetDescription>
            </SheetHeader>
            <ControllerPanel chat={chat} className="h-full rounded-none border-0" headerClassName="pr-12" />
          </SheetContent>
        </Sheet>
      )}

      <MonitorRunSheet
        uid={search.run}
        run={openRun}
        scope={scope}
        onClose={() => setSearch({ run: undefined })}
      />
    </PageContainer>
  )
}
